import { ErrorMessage } from "@hookform/error-message";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useForm, useWatch } from "react-hook-form";
import shipCore from "../../core/ship.core";
import { useTranslation } from "../../core/translation.core";
import { SfDistrict } from "../../types/shipSetting";
import { LabelAndInput, LabelAndSelect, OptionItem } from "../reuseables/form.comp";

interface SfForm {
  region: string;
  district: string;
  subDistrict: string;
  code: string;
  sfRecipientName: string;
  sfRecipientTelCountryCode: string;
  sfRecipientTel: string;
}

const FormSfLocation = ({ methods }: { methods: UseFormReturn<any, any> }) => {
  const { locale, isReady } = useRouter();

  const subDistrict = useWatch({
    name: "subDistrict",
    control: methods.control,
  });

  const {
    data: locations,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["sfLocation", subDistrict, locale],
    queryFn: (context) => shipCore.getSfLocations(context.queryKey[1]!),
    enabled: isReady && !!subDistrict,
  });

  const updateSfLocation = (code: string) => {
    methods.setValue("sfCode", code);
  };

  methods.register("sfCode", { required: "Please select a location" });
  const selectedCode = useWatch({ name: "sfCode", control: methods.control });

  if (isLoading || isError || !locations) {
    return <div>Loading locations...</div>;
  }

  return (
    <div>
      {locations.map((l) => (
        <div
          key={l.code}
          className={`p-4 border cursor-pointer relative ${l.code === selectedCode ? "border-blue-400 border-4" : ""}`}
          data-code={l.code}
          onClick={() => updateSfLocation(l.code)}
        >
          <p>{l.code}</p>
          <p>{l.address}</p>
        </div>
      ))}
      <ErrorMessage
        name="sfCode"
        errors={methods.formState.errors}
        render={({ message }) => <p className="text-red-400 text-right text-xs">{message}</p>}
      />
    </div>
  );
};

const FormSfRegionSelect = ({ subDistricts, methods }: { subDistricts: SfDistrict[]; methods: UseFormReturn<any, any> }) => {
  const { t, msg } = useTranslation();

  const distinctRegions = [...new Set(subDistricts.map((d) => d.region))];
  const options = distinctRegions.map((r) => ({
    label: r,
    value: r,
  }));

  return <LabelAndSelect methods={methods} fieldName="region" label={t(msg.ship.region)} options={options} />;
};

function FormSelectTriggerPopulate<T extends FieldValues>({
  methods,
  watchField,
  populateField,
  label,
  getOptions,
}: {
  methods: UseFormReturn<T, any>;
  watchField: Path<T>;
  populateField: Path<T>;
  label: string;
  getOptions: (watchValue: PathValue<T, Path<T>>) => OptionItem[];
}) {
  const watchValue = useWatch({
    name: watchField,
    control: methods.control,
  });

  const options = useMemo(() => getOptions(watchValue), [watchValue, getOptions]);

  useEffect(() => {
    if (options && options.length > 0) {
      methods.setValue(populateField, options[0].value as any);
    } else {
      methods.setValue(populateField, "" as any);
    }
  }, [options, methods, populateField]);

  return <LabelAndSelect methods={methods} fieldName={populateField} label={label} options={options} />;
}

export default function ShipInfoCreatorSf({ methods }: { methods: UseFormReturn<any> }) {
  const { t, msg } = useTranslation();
  const { locale, isReady } = useRouter();

  const {
    data: subDistricts,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["getSfSubDistricts", locale],
    queryFn: shipCore.getSfSubDistricts,
    enabled: isReady,
  });

  useEffect(() => {
    if (!isLoading && !isError && subDistricts) {
      methods.setValue("region", subDistricts[0].region);
    }
  }, [isLoading, isError, subDistricts, methods]);

  if (isError || isLoading || !subDistricts) {
    return <div>Loading...</div>;
  }

  function getDistrictOptions(selectedRegion: string): OptionItem[] {
    const districts = [...new Set(subDistricts?.filter((d) => d.region === selectedRegion).map((d) => d.district))];
    return districts.map((d) => ({
      label: d,
      value: d,
    }));
  }

  return (
    <>
      <LabelAndInput type="text"  register={methods.register} fieldName="sfRecipientName" label={t(msg.ship.recipientName)} />

      <LabelAndInput
        register={methods.register}
        fieldName="sfRecipientTelCountryCode"
        label={t(msg.ship.telCountryCode)}
        type="number"
      />

      <LabelAndInput register={methods.register} fieldName="sfRecipientTel" label={t(msg.pickupAddr.tel)} type="tel" />

      <FormSfRegionSelect subDistricts={subDistricts} methods={methods} />

      <FormSelectTriggerPopulate
        methods={methods}
        watchField="region"
        populateField="district"
        label={t(msg.ship.district)}
        getOptions={getDistrictOptions}
      />

      <FormSelectTriggerPopulate
        methods={methods}
        watchField="district"
        populateField="subDistrict"
        label={t(msg.ship.subDistrict)}
        getOptions={(watchValue) => [
          ...new Set(
            subDistricts
              .filter((d) => d.district === watchValue)
              .map((d) => ({
                label: d.subDistrict,
                value: d.key,
              }))
          ),
        ]}
      />

      <FormSfLocation methods={methods} />
    </>
  );
}
